import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../components/AdminLayout";
import "./Notifications.css";

const API_BASE = process.env.REACT_APP_API_URL;
const ORG_ID = "resh-dev";
const PAGE_SIZE = 25;

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
    OrgId: ORG_ID,
  };
}

// ── Format helpers ─────────────────────────────────────────────────────────────
function formatTimeOnly(dateVal) {
  if (!dateVal) return "—";
  let d;
  if (typeof dateVal === "string") d = new Date(dateVal);
  else if (dateVal.$date) d = new Date(dateVal.$date);
  else d = new Date(dateVal);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
}

function formatDateOnly(dateVal) {
  if (!dateVal) return "";
  let d;
  if (typeof dateVal === "string") d = new Date(dateVal);
  else if (dateVal.$date) d = new Date(dateVal.$date);
  else d = new Date(dateVal);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function formatCurrency(val) {
  if (val === undefined || val === null) return "—";
  const num = Number(val);
  if (isNaN(num)) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(num);
}

function formatCooldown(nextRetryAt) {
  if (!nextRetryAt) return null;
  const d = new Date(typeof nextRetryAt === "string" ? nextRetryAt : nextRetryAt.$date || nextRetryAt);
  if (isNaN(d.getTime())) return null;
  const diffMs = d.getTime() - Date.now();
  if (diffMs <= 0) return "Ready to retry";
  const diffMins = Math.ceil(diffMs / 60000);
  if (diffMins < 60) return `Retry in ${diffMins}m`;
  const hrs = Math.floor(diffMins / 60);
  const mins = diffMins % 60;
  return `Retry in ${hrs}h ${mins}m`;
}

// ── SVG icon helpers ───────────────────────────────────────────────────────────
function IconCalendar({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}
function IconBell({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}
function IconFileText({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}
function IconZap({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}
function IconCheck({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function IconCreditCard({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  );
}
function IconStar({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}
function IconClock({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
function IconMapPin({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
function IconPhone({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}
function IconShield({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
function IconAlertTriangle({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
function IconRotateCw({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  );
}

// ── Render Type Badge ──────────────────────────────────────────────────────────
function renderTypeBadge(type) {
  switch (type) {
    case "CustomerReminder":
      return (
        <span className="notif-type-badge notif-type-badge--reminder">
          <IconCalendar size={12} /> Event Reminder
        </span>
      );
    case "AdminReminder":
      return (
        <span className="notif-type-badge notif-type-badge--admin-reminder">
          <IconBell size={12} /> Admin Reminder
        </span>
      );
    case "BookingCreated":
      return (
        <span className="notif-type-badge notif-type-badge--created">
          <IconFileText size={12} /> Booking Request
        </span>
      );
    case "NewBookingAdmin":
      return (
        <span className="notif-type-badge notif-type-badge--new-admin">
          <IconZap size={12} /> New Alert (Admin)
        </span>
      );
    case "BookingConfirmed":
      return (
        <span className="notif-type-badge notif-type-badge--confirmed">
          <IconCheck size={12} /> Confirmed
        </span>
      );
    case "AdvancePaymentReceived":
      return (
        <span className="notif-type-badge notif-type-badge--payment">
          <IconCreditCard size={12} /> Advance Paid
        </span>
      );
    case "PaymentCompleted":
      return (
        <span className="notif-type-badge notif-type-badge--completed">
          <IconCheck size={12} /> Paid in Full
        </span>
      );
    case "BookingCompleted":
      return (
        <span className="notif-type-badge notif-type-badge--completed">
          <IconStar size={12} /> Completed
        </span>
      );
    default:
      return (
        <span className="notif-type-badge notif-type-badge--default">
          <IconBell size={12} /> {type || "Notification"}
        </span>
      );
  }
}

// ── Dropdown Component (matching Bookings exactly) ─────────────────────────────
function FilterDropdown({ label, options, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className="booking-dropdown" ref={ref}>
      <button
        type="button"
        className={`booking-dropdown__trigger ${isOpen ? "booking-dropdown__trigger--open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{selectedOption ? selectedOption.label : label}</span>
        <svg
          className="booking-dropdown__chevron"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          width="13"
          height="13"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <ul className="booking-dropdown__menu">
          {options.map((opt) => (
            <li
              key={opt.value}
              className={`booking-dropdown__item ${value === opt.value ? "booking-dropdown__item--active" : ""}`}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              <span>{opt.label}</span>
              {value === opt.value && (
                <svg
                  className="booking-dropdown__check"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  width="12"
                  height="12"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Numbered Pagination Component ─────────────────────────────────────────────
function PaginationControls({ currentPage, totalPages, totalDocs, pageSize, onPageChange, loading }) {
  const startDoc = totalDocs === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endDoc = Math.min(currentPage * pageSize, totalDocs);
  const isDisabled = loading || totalPages <= 1;

  const pages = [];
  const delta = 1;
  const left = Math.max(1, currentPage - delta);
  const right = Math.min(totalPages, currentPage + delta);

  for (let i = left; i <= right; i++) {
    pages.push(i);
  }

  return (
    <div className="bookings-pagination">
      <span className="bookings-pagination__info">
        {loading
          ? "Loading…"
          : totalDocs === 0
            ? "No results"
            : `Showing ${startDoc}–${endDoc} of ${totalDocs} records`}
      </span>
      <div className="bookings-pagination__controls">
        <button className="bookings-pagination__btn" disabled={currentPage === 1 || isDisabled} onClick={() => onPageChange(1)} title="First page">«</button>
        <button className="bookings-pagination__btn" disabled={currentPage === 1 || isDisabled} onClick={() => onPageChange(currentPage - 1)} title="Previous page">‹</button>

        {left > 1 && (
          <>
            <button className="bookings-pagination__btn" disabled={isDisabled} onClick={() => onPageChange(1)}>1</button>
            {left > 2 && <span className="bookings-pagination__ellipsis">…</span>}
          </>
        )}

        {pages.map((p) => (
          <button
            key={p}
            className={`bookings-pagination__btn ${p === currentPage ? "bookings-pagination__btn--active" : ""}`}
            disabled={loading}
            onClick={() => onPageChange(p)}
          >
            {p}
          </button>
        ))}

        {right < totalPages && (
          <>
            {right < totalPages - 1 && <span className="bookings-pagination__ellipsis">…</span>}
            <button className="bookings-pagination__btn" disabled={isDisabled} onClick={() => onPageChange(totalPages)}>{totalPages}</button>
          </>
        )}

        <button className="bookings-pagination__btn" disabled={currentPage === totalPages || isDisabled} onClick={() => onPageChange(currentPage + 1)} title="Next page">›</button>
        <button className="bookings-pagination__btn" disabled={currentPage === totalPages || isDisabled} onClick={() => onPageChange(totalPages)} title="Last page">»</button>
      </div>
    </div>
  );
}

// ── Filter options ─────────────────────────────────────────────────────────────
const TYPE_OPTIONS = [
  { value: "All", label: "All Types" },
  { value: "CustomerReminder", label: "Event Reminder (Client)" },
  { value: "AdminReminder", label: "Event Reminder (Admin)" },
  { value: "BookingCreated", label: "Booking Request" },
  { value: "NewBookingAdmin", label: "New Booking Alert (Admin)" },
  { value: "BookingConfirmed", label: "Booking Confirmed" },
  { value: "AdvancePaymentReceived", label: "Advance Payment" },
  { value: "PaymentCompleted", label: "Payment Complete" },
  { value: "BookingCompleted", label: "Booking Completed" },
];



const DELIVERY_OPTIONS = [
  { value: "All", label: "All Delivery States" },
  { value: "delivered", label: "Delivered" },
  { value: "read", label: "Read" },
  { value: "sent", label: "Sent" },
  { value: "failed", label: "Failed" },
];

const ERROR_OPTIONS = [
  { value: "All", label: "All Error Codes" },
  { value: "FREQUENCY_CAP", label: "131049 Frequency Cap" },
  { value: "BILLING", label: "131042 Billing Issue" },
  { value: "AUTH", label: "190 Invalid Token" },
  { value: "RATE_LIMIT", label: "Rate Limits" },
  { value: "PERMANENT", label: "Permanent Errors" },
];

const RECIPIENT_OPTIONS = [
  { value: "All", label: "All Recipients" },
  { value: "Customer", label: "Customer" },
  { value: "Admin", label: "Admin" },
];

// ── Main component ─────────────────────────────────────────────────────────────
export default function Notifications() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_logs: 0,
    total_success: 0,
    total_delivered: 0,
    total_read: 0,
    total_failed: 0,
    freq_cap_count: 0,
    billing_failed_count: 0,
    success_rate: "0.0",
    pending_retries: 0,
    today_cust_reminders: 0,
    today_admin_reminders: 0,
    today_events_count: 0,
    account_paused: false,
    account_pause_reason: "",
    quality_rating: "GREEN",
    phone_status: "CONNECTED",
    messaging_tier: "TIER_1K",
    billing_status: "ACTIVE",
  });

  // Account Health & Alerts
  const [health, setHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [resumingSending, setResumingSending] = useState(false);
  const [alerts, setAlerts] = useState([]);

  // Filter & Search states
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [deliveryFilter, setDeliveryFilter] = useState("All");
  const [errorFilter, setErrorFilter] = useState("All");
  const [recipientFilter, setRecipientFilter] = useState("All");

  // Mobile filter modal
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [tempTypeFilter, setTempTypeFilter] = useState("All");
  const [tempStatusFilter, setTempStatusFilter] = useState("All");
  const [tempDeliveryFilter, setTempDeliveryFilter] = useState("All");
  const [tempErrorFilter, setTempErrorFilter] = useState("All");
  const [tempRecipientFilter, setTempRecipientFilter] = useState("All");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);

  // Booking detail modal
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loadingBooking, setLoadingBooking] = useState(false);

  // Today's Reminders modal
  const [showTodayRemindersModal, setShowTodayRemindersModal] = useState(false);
  const [loadingTodayReminders, setLoadingTodayReminders] = useState(false);
  const [todayReminders, setTodayReminders] = useState([]);
  const [todayRemindersDate, setTodayRemindersDate] = useState("");

  // Action states
  const [triggeringReminders, setTriggeringReminders] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);

  const toastTimeoutRef = useRef(null);

  const showToast = useCallback((msg) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToastMessage(msg);
    toastTimeoutRef.current = setTimeout(() => setToastMessage(null), 3500);
  }, []);

  const hasActiveFilters =
    typeFilter !== "All" ||
    statusFilter !== "All" ||
    deliveryFilter !== "All" ||
    errorFilter !== "All" ||
    recipientFilter !== "All" ||
    searchTerm.trim();

  // 1. Fetch Stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/notifications/stats`, { headers: authHeaders() });
      if (res.ok) {
        const json = await res.json();
        if (json?.data) setStats(json.data);
      }
    } catch (err) {
      console.warn("Could not fetch notification stats:", err);
    }
  }, []);

  // 2. Fetch Account Health from Meta
  const fetchHealth = useCallback(async () => {
    setLoadingHealth(true);
    try {
      const res = await fetch(`${API_BASE}/notifications/health`, { headers: authHeaders() });
      if (res.ok) {
        const json = await res.json();
        if (json?.data) setHealth(json.data);
      }
    } catch (err) {
      console.warn("Could not fetch account health:", err);
    } finally {
      setLoadingHealth(false);
    }
  }, []);

  // 3. Fetch Alerts
  const fetchAlerts = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/notifications/alerts`, { headers: authHeaders() });
      if (res.ok) {
        const json = await res.json();
        if (json?.data?.alerts) setAlerts(json.data.alerts);
      }
    } catch (err) {
      console.warn("Could not fetch alerts:", err);
    }
  }, []);

  // 4. Fetch Logs (server-side filter)
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const start = (currentPage - 1) * PAGE_SIZE;
      const end = start + PAGE_SIZE;

      const conditions = [];

      if (typeFilter !== "All") {
        conditions.push({ column: "notification_type", operator: "EQUALS", type: "string", value: typeFilter });
      }
      if (statusFilter !== "All") {
        conditions.push({ column: "status", operator: "EQUALS", type: "string", value: statusFilter });
      }
      if (deliveryFilter !== "All") {
        conditions.push({ column: "delivery_status", operator: "EQUALS", type: "string", value: deliveryFilter });
      }
      if (errorFilter !== "All") {
        conditions.push({ column: "error_category", operator: "EQUALS", type: "string", value: errorFilter });
      }
      if (recipientFilter !== "All") {
        conditions.push({ column: "recipient_type", operator: "EQUALS", type: "string", value: recipientFilter });
      }

      const bodyPayload = {
        start,
        end,
        sort: [{ colId: "created_at", sort: "desc" }],
      };

      if (conditions.length > 0) {
        bodyPayload.filter = [{ clause: "AND", conditions }];
      }

      const res = await fetch(`${API_BASE}/entities/filter/notification_logs`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(bodyPayload),
      });

      if (!res.ok) throw new Error("Failed to load notification logs");

      const data = await res.json();
      const records = data?.data?.[0]?.response ?? [];
      const total = data?.data?.[0]?.pagination?.[0]?.totalDocs ?? records.length;

      setLogs(records);
      setTotalDocs(total);
    } catch (err) {
      console.error(err);
      showToast("Error loading notification logs: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, typeFilter, statusFilter, deliveryFilter, errorFilter, recipientFilter, showToast]);

  useEffect(() => { fetchStats(); fetchHealth(); fetchAlerts(); }, [fetchStats, fetchHealth, fetchAlerts]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Actions
  const handleResumeSending = async () => {
    setResumingSending(true);
    try {
      const res = await fetch(`${API_BASE}/notifications/health/resume`, {
        method: "POST",
        headers: authHeaders(),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error_msg || "Failed to resume sending");
      showToast("WhatsApp notifications resumed successfully!");
      fetchHealth();
      fetchStats();
      fetchAlerts();
    } catch (err) {
      showToast("Resume failed: " + err.message);
    } finally {
      setResumingSending(false);
    }
  };

  const handleCheckHealth = async () => {
    setLoadingHealth(true);
    try {
      const res = await fetch(`${API_BASE}/notifications/health/check`, {
        method: "POST",
        headers: authHeaders(),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error_msg || "Health check failed");
      if (json?.data) setHealth(json.data);
      showToast("Account health refreshed from Meta Cloud API");
      fetchStats();
    } catch (err) {
      showToast("Health check failed: " + err.message);
    } finally {
      setLoadingHealth(false);
    }
  };

  const handleDismissAlert = async (alertId) => {
    try {
      await fetch(`${API_BASE}/notifications/alerts/${alertId}/dismiss`, {
        method: "POST",
        headers: authHeaders(),
      });
      setAlerts((prev) => prev.filter((a) => a._id !== alertId));
      showToast("Alert dismissed");
    } catch (err) {
      showToast("Failed to dismiss alert: " + err.message);
    }
  };

  const handleRetryLog = async (logId) => {
    if (!logId) return;
    setRetryingId(logId);
    try {
      const res = await fetch(`${API_BASE}/notifications/retry/${logId}`, {
        method: "POST",
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error_msg || "Retry failed");
      showToast("Message re-queued for delivery!");
      setTimeout(() => { fetchLogs(); fetchStats(); }, 1200);
    } catch (err) {
      showToast("Retry failed: " + err.message);
    } finally {
      setRetryingId(null);
    }
  };

  // Client-side search on current page
  const filteredLogs = logs.filter((item) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      String(item.booking_id || "").toLowerCase().includes(term) ||
      String(item.phone_number || "").toLowerCase().includes(term) ||
      String(item.error_message || "").toLowerCase().includes(term) ||
      String(item.error_details || "").toLowerCase().includes(term) ||
      String(item.whatsapp_message_id || "").toLowerCase().includes(term) ||
      String(item.customer_id || "").toLowerCase().includes(term)
    );
  });

  const handleClearFilters = () => {
    setTypeFilter("All");
    setStatusFilter("All");
    setDeliveryFilter("All");
    setErrorFilter("All");
    setRecipientFilter("All");
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Fetch booking details
  const handleViewBooking = async (bookingId) => {
    if (!bookingId) return;
    setLoadingBooking(true);
    setSelectedBooking({ loading: true, booking_code: bookingId });
    try {
      const res = await fetch(`${API_BASE}/notifications/booking/${encodeURIComponent(bookingId)}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error_msg || "Failed to load booking details");
      setSelectedBooking(data?.data);
    } catch (err) {
      showToast("Could not load booking details: " + err.message);
      setSelectedBooking(null);
    } finally {
      setLoadingBooking(false);
    }
  };

  // Open Today's Reminders Modal
  const handleOpenTodayReminders = async () => {
    setLoadingTodayReminders(true);
    setShowTodayRemindersModal(true);
    try {
      const res = await fetch(`${API_BASE}/notifications/today-reminders`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error_msg || "Failed to fetch today's reminders");
      setTodayReminders(data?.data?.reminders || []);
      setTodayRemindersDate(data?.data?.date || "");
    } catch (err) {
      showToast("Error loading today's reminders: " + err.message);
      setTodayReminders([]);
    } finally {
      setLoadingTodayReminders(false);
    }
  };

  // Send Today's Reminders
  const handleTriggerReminders = async () => {
    setTriggeringReminders(true);
    try {
      const res = await fetch(`${API_BASE}/notifications/trigger-reminders`, {
        method: "POST",
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error_msg || "Trigger failed");
      const count = data?.data?.bookings_found ?? 0;
      showToast(`Today's reminders sent! ${count} booking(s) evaluated and queued.`);
      setShowTodayRemindersModal(false);
      setTimeout(() => { fetchLogs(); fetchStats(); }, 1500);
    } catch (err) {
      showToast("Trigger failed: " + err.message);
    } finally {
      setTriggeringReminders(false);
    }
  };

  const totalPages = Math.ceil(totalDocs / PAGE_SIZE) || 1;

  // Resolved Health states
  const isPaused = stats.account_paused || health?.paused;
  const pauseReason = stats.account_pause_reason || health?.pause_reason;
  const qualityRating = health?.quality_rating || stats.quality_rating || "GREEN";
  const phoneStatus = health?.phone_status || stats.phone_status || "CONNECTED";
  const billingStatus = health?.billing_status || stats.billing_status || "ACTIVE";
  const messagingTier = health?.messaging_limit_tier || stats.messaging_tier || "TIER_1K";

  return (
    <AdminLayout>
      <div className="notifications-page">

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <header className="notifications-page__header">
          <div className="notifications-page__title-area">
            <h1 className="notifications-page__title">WhatsApp Notification Logs</h1>
            <p className="notifications-page__subtitle">
              Live delivery audit trail, Meta Cloud API billing status, rate limiting &amp; automated cooldown queue.
            </p>
          </div>

          <div className="notifications-page__actions">
            <button
              className="notif-btn notif-btn--secondary"
              onClick={() => { fetchStats(); fetchHealth(); fetchAlerts(); fetchLogs(); showToast("Refreshed notification logs & health"); }}
              disabled={loading || loadingHealth}
              title="Refresh logs, health and metrics"
            >
              <svg className={`notif-btn__icon ${loading || loadingHealth ? "notif-btn__icon--spin" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
              </svg>
              Refresh
            </button>

            <button className="notif-btn notif-btn--primary" onClick={handleOpenTodayReminders}>
              <svg className="notif-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              Today's Reminders
            </button>
          </div>
        </header>

        {/* ── Active Account Alerts Banner (Billing / Token / Frequency Limits) ── */}
        {alerts.length > 0 && (
          <div className="notif-alerts-container">
            {alerts.map((al) => (
              <div key={al._id} className={`notif-alert-banner notif-alert-banner--${al.severity.toLowerCase()}`}>
                <div className="notif-alert-banner__icon">
                  <IconAlertTriangle size={18} />
                </div>
                <div className="notif-alert-banner__content">
                  <div className="notif-alert-banner__title">{al.title}</div>
                  <div className="notif-alert-banner__message">{al.message}</div>
                </div>
                <div className="notif-alert-banner__actions">
                  {isPaused && (
                    <button
                      className="notif-alert-banner__btn notif-alert-banner__btn--resume"
                      onClick={handleResumeSending}
                      disabled={resumingSending}
                    >
                      {resumingSending ? "Resuming…" : "Resume Sending"}
                    </button>
                  )}
                  <button
                    className="notif-alert-banner__btn notif-alert-banner__btn--dismiss"
                    onClick={() => handleDismissAlert(al._id)}
                    title="Dismiss alert"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Account Health & Meta Cloud API Billing Hub ─────────────────────── */}
        <section className={`notif-health-hub ${isPaused ? "notif-health-hub--paused" : ""}`}>
          <div className="notif-health-hub__header">
            <div className="notif-health-hub__title-wrap">
              <IconShield size={18} />
              <span className="notif-health-hub__title">WhatsApp Cloud API &amp; Account Health</span>
              <span className={`notif-health-pill notif-health-pill--${isPaused ? "paused" : qualityRating.toLowerCase()}`}>
                <span className="notif-status-dot" />
                {isPaused ? "PAUSED" : phoneStatus === "CONNECTED" ? "HEALTHY" : phoneStatus}
              </span>
            </div>

            <div className="notif-health-hub__actions">
              {isPaused && (
                <button
                  className="notif-btn notif-btn--primary notif-btn--sm"
                  onClick={handleResumeSending}
                  disabled={resumingSending}
                >
                  {resumingSending ? "Resuming…" : "Resume Sending"}
                </button>
              )}
              <button
                className="notif-btn notif-btn--secondary notif-btn--sm"
                onClick={handleCheckHealth}
                disabled={loadingHealth}
                title="Query supported Meta Graph APIs for real-time status"
              >
                <IconRotateCw size={12} className={loadingHealth ? "notif-btn__icon--spin" : ""} />
                Check Health
              </button>
            </div>
          </div>

          <div className="notif-health-grid">
            {/* Meta Quality Rating */}
            <div className="notif-health-item">
              <span className="notif-health-item__label">Quality Rating</span>
              <div className="notif-health-item__value">
                <span className={`notif-badge-pill notif-badge-pill--${qualityRating === "GREEN" ? "green" : qualityRating === "YELLOW" ? "yellow" : "red"}`}>
                  {qualityRating === "GREEN" ? "HIGH (GREEN)" : qualityRating === "YELLOW" ? "MEDIUM (YELLOW)" : qualityRating}
                </span>
              </div>
              <span className="notif-health-item__desc">7-day Meta spam &amp; delivery health</span>
            </div>

            {/* Connection Status */}
            <div className="notif-health-item">
              <span className="notif-health-item__label">Phone Connection</span>
              <div className="notif-health-item__value">
                <span className={`notif-badge-pill notif-badge-pill--${phoneStatus === "CONNECTED" ? "green" : "red"}`}>
                  {phoneStatus}
                </span>
              </div>
              <span className="notif-health-item__desc">Meta phone number connectivity</span>
            </div>

            {/* Billing / Payment Status */}
            <div className="notif-health-item">
              <span className="notif-health-item__label">Billing &amp; Payment</span>
              <div className="notif-health-item__value">
                <span className={`notif-badge-pill notif-badge-pill--${billingStatus === "ACTIVE" ? "green" : "red"}`}>
                  {billingStatus === "ACTIVE" ? "Active (Normal)" : "Payment Issue [131042]"}
                </span>
              </div>
              <span className="notif-health-item__desc">
                {billingStatus === "ACTIVE" ? "Credit line in good standing" : "Card or credit line exhausted"}
              </span>
            </div>

            {/* Messaging Tier */}
            <div className="notif-health-item">
              <span className="notif-health-item__label">Messaging Limit Tier</span>
              <div className="notif-health-item__value">
                <span className="notif-tier-badge">
                  {messagingTier.replace("TIER_", "")} / 24h
                </span>
              </div>
              <span className="notif-health-item__desc">Meta 24h conversation quota</span>
            </div>
          </div>

          {/* Account Paused Notice */}
          {isPaused && (
            <div className="notif-paused-notice">
              <IconAlertTriangle size={15} />
              <span>
                <strong>Outbound Notifications Paused:</strong> {pauseReason || "Account-level billing or authentication restriction."}
                {" "}Please verify your billing method in Meta Business Manager, then click <strong>Resume Sending</strong>.
              </span>
            </div>
          )}
        </section>

        {/* ── Stat Cards ─────────────────────────────────────────────────────── */}
        <div className="notif-stats">
          <div className="notif-stat-card">
            <div className="notif-stat-card__lbl">
              <span>Total Dispatches</span>
              <span className="notif-stat-card__badge notif-stat-card__badge--green">{stats.success_rate}%</span>
            </div>
            <div className="notif-stat-card__val">{stats.total_logs}</div>
            <div className="notif-stat-card__meta">All notification audit records</div>
          </div>

          <div className="notif-stat-card notif-stat-card--success">
            <div className="notif-stat-card__lbl">Delivered</div>
            <div className="notif-stat-card__val">{stats.total_delivered || stats.total_success}</div>
            <div className="notif-stat-card__meta">
              Delivered: {stats.total_delivered || stats.total_success} &middot; Read: {stats.total_read || 0}
            </div>
          </div>

          <div className={`notif-stat-card ${stats.total_failed > 0 ? "notif-stat-card--danger" : ""}`}>
            <div className="notif-stat-card__lbl">
              <span>Failed Messages</span>
              {stats.total_failed > 0 && (
                <span className="notif-stat-card__badge notif-stat-card__badge--red">Needs Attention</span>
              )}
            </div>
            <div className="notif-stat-card__val">{stats.total_failed}</div>
            <div className="notif-stat-card__meta">
              Billing failures: {stats.billing_failed_count || 0}
            </div>
          </div>

          <div className="notif-stat-card notif-stat-card--freq">
            <div className="notif-stat-card__lbl">
              <span>Frequency Capped</span>
              <span className="notif-stat-card__badge notif-stat-card__badge--amber">131049</span>
            </div>
            <div className="notif-stat-card__val">{stats.freq_cap_count || 0}</div>
            <div className="notif-stat-card__meta">Protected by controlled cooldown</div>
          </div>

          <div className="notif-stat-card">
            <div className="notif-stat-card__lbl">Pending Retries</div>
            <div className="notif-stat-card__val">{stats.pending_retries}</div>
            <div className="notif-stat-card__meta">Cooldown scheduled queue</div>
          </div>
        </div>

        {/* ── Filters — matching Bookings pattern ────────────────────────────── */}
        <section className="booking-filters">

          {/* Search */}
          <div className="booking-filters__field booking-filters__field--search">
            <span className="booking-filters__label">Search</span>
            <div className="booking-search-input-wrapper">
              <svg className="booking-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Booking ID, phone, message ID…"
                className="booking-filters__input booking-filters__input--has-icon"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Mobile filter button */}
          <div className="booking-filters__mobile-btn-wrap">
            <button
              type="button"
              className={`booking-filters__mobile-btn${hasActiveFilters ? " booking-filters__mobile-btn--active" : ""}`}
              onClick={() => {
                setTempTypeFilter(typeFilter);
                setTempDeliveryFilter(deliveryFilter);
                setTempErrorFilter(errorFilter);
                setTempRecipientFilter(recipientFilter);
                setShowFilterModal(true);
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              <span>Filters</span>
              {hasActiveFilters && <span className="booking-filters__mobile-dot" />}
            </button>

            {hasActiveFilters && (
              <button
                type="button"
                className="booking-btn booking-btn--danger"
                onClick={handleClearFilters}
                style={{ height: "36px", padding: "0 0.75rem", fontSize: "0.75rem" }}
              >
                ✕ Clear
              </button>
            )}
          </div>

          {/* Desktop Filter group — same as Bookings */}
          <div className="booking-filters__group booking-filters__group--desktop">
            <div className="booking-filters__field booking-filters__field--filter">
              <span className="booking-filters__label">Notification Type</span>
              <FilterDropdown
                label="All Types"
                value={typeFilter}
                options={TYPE_OPTIONS}
                onChange={(val) => { setTypeFilter(val); setCurrentPage(1); }}
              />
            </div>

            <div className="booking-filters__field booking-filters__field--filter">
              <span className="booking-filters__label">Delivery Status</span>
              <FilterDropdown
                label="All Delivery States"
                value={deliveryFilter}
                options={DELIVERY_OPTIONS}
                onChange={(val) => { setDeliveryFilter(val); setCurrentPage(1); }}
              />
            </div>

            <div className="booking-filters__field booking-filters__field--filter">
              <span className="booking-filters__label">Error Code</span>
              <FilterDropdown
                label="All Error Codes"
                value={errorFilter}
                options={ERROR_OPTIONS}
                onChange={(val) => { setErrorFilter(val); setCurrentPage(1); }}
              />
            </div>

            <div className="booking-filters__field booking-filters__field--filter">
              <span className="booking-filters__label">Recipient</span>
              <FilterDropdown
                label="All Recipients"
                value={recipientFilter}
                options={RECIPIENT_OPTIONS}
                onChange={(val) => { setRecipientFilter(val); setCurrentPage(1); }}
              />
            </div>

            {hasActiveFilters && (
              <div className="booking-filters__field booking-filters__field--clear">
                <span className="booking-filters__label">&nbsp;</span>
                <button
                  type="button"
                  className="booking-btn booking-btn--danger"
                  onClick={handleClearFilters}
                  title="Reset all filters"
                  style={{ height: "36px", padding: "0 0.75rem", fontSize: "0.75rem" }}
                >
                  ✕ Clear
                </button>
              </div>
            )}
          </div>
        </section>

        {/* ── Table Card ──────────────────────────────────────────────────────── */}
        <div className="notif-table-card">
          <div className="notif-table-wrapper">
            <table className="notif-table">
              <thead>
                <tr>
                  <th style={{ width: "12%" }}>Delivery</th>
                  <th style={{ width: "18%" }}>Notification Type</th>
                  <th style={{ width: "18%" }}>Recipient</th>
                  <th style={{ width: "24%" }}>Error Code / Status</th>
                  <th style={{ width: "14%" }}>Dispatched At</th>
                  <th style={{ width: "14%", textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6">
                      <div className="app-loading-placeholder">
                        <div className="app-spinner" />
                        <span className="app-loading-placeholder__text">Loading notification logs...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan="6">
                      <div className="notif-empty">
                        <svg className="notif-empty__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <rect x="2" y="4" width="20" height="16" rx="2" />
                          <path d="M22 7l-10 7L2 7" />
                        </svg>
                        <h3 className="notif-empty__title">No Notification Logs Found</h3>
                        <p className="notif-empty__text">
                          {hasActiveFilters
                            ? "Try adjusting your filters or search terms."
                            : "WhatsApp notification audit records will appear here as bookings, reminders, and payments trigger."}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((item) => {
                    const cooldownText = formatCooldown(item.next_retry_at);
                    const isFreqCap = item.error_code === 131049 || item.error_category === "FREQUENCY_CAP";
                    const isBilling = item.error_code === 131042 || item.error_category === "BILLING";
                    const isAuth = item.error_code === 190 || item.error_category === "AUTH";
                    const isRateLimit = item.error_category === "RATE_LIMIT";

                    return (
                      <tr key={item._id}>
                        {/* Status & Delivery State */}
                        <td>
                          <div className="notif-delivery-cell">
                            {item.status === "SUCCESS" ? (
                              <span className="notif-status-badge notif-status-badge--success">
                                <span className="notif-status-dot" />
                                {item.delivery_status === "read"
                                  ? "READ"
                                  : item.delivery_status === "delivered"
                                    ? "DELIVERED"
                                    : "SENT"}
                              </span>
                            ) : (
                              <span className="notif-status-badge notif-status-badge--failed">
                                <span className="notif-status-dot" />
                                FAILED
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Type */}
                        <td>{renderTypeBadge(item.notification_type)}</td>

                        {/* Recipient */}
                        <td>
                          <div className="notif-contact-cell">
                            <div className="notif-contact-top">
                              <span className={`notif-recipient-pill ${item.recipient_type === "Admin" ? "notif-recipient-pill--admin" : ""}`}>
                                {item.recipient_type || "Customer"}
                              </span>
                              <span className="notif-phone">
                                {item.phone_number ? `+${item.phone_number}` : "—"}
                              </span>
                            </div>

                          </div>
                        </td>

                        {/* Error Code & Details / Cooldown */}
                        <td>
                          <div className="notif-error-cell">
                            {item.status === "FAILED" ? (
                              <>
                                <div className="notif-error-tags">
                                  {isFreqCap && (
                                    <span className="notif-err-pill notif-err-pill--freq" title="Meta Marketing Message Frequency Cap">
                                      131049 Frequency Cap
                                    </span>
                                  )}
                                  {isBilling && (
                                    <span className="notif-err-pill notif-err-pill--billing" title="Meta Payment / Insufficient Balance Issue">
                                      131042 Payment Issue
                                    </span>
                                  )}
                                  {isAuth && (
                                    <span className="notif-err-pill notif-err-pill--auth" title="Meta Access Token Expired">
                                      190 Token Expired
                                    </span>
                                  )}
                                  {isRateLimit && (
                                    <span className="notif-err-pill notif-err-pill--rate">
                                      Rate Limit Hit
                                    </span>
                                  )}
                                  {item.is_permanent_fail && (
                                    <span className="notif-err-pill notif-err-pill--permanent">
                                      Permanent Error
                                    </span>
                                  )}
                                  {!isFreqCap && !isBilling && !isAuth && !isRateLimit && !item.is_permanent_fail && item.error_code > 0 && (
                                    <span className="notif-err-pill notif-err-pill--generic">
                                      Code {item.error_code}
                                    </span>
                                  )}
                                </div>

                                <div className="notif-error-desc" title={item.error_details || item.error_message}>
                                  {item.error_details || item.error_message || "Message delivery failed"}
                                </div>

                                {cooldownText && (
                                  <div className="notif-cooldown-timer">
                                    <IconClock size={11} />
                                    <span>{cooldownText}</span>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="notif-success-meta">
                                <span className="notif-msg-id" title={item.whatsapp_message_id}>
                                  {item.whatsapp_message_id ? `ID: ${item.whatsapp_message_id.slice(0, 18)}…` : "Delivered via Meta"}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Time */}
                        <td>
                          <div className="notif-time-cell">
                            <span className="notif-time-primary">{formatTimeOnly(item.sent_at || item.created_at)}</span>
                            <span className="notif-time-secondary">{formatDateOnly(item.sent_at || item.created_at)}</span>
                          </div>
                        </td>

                        {/* Actions */}
                        <td style={{ textAlign: "right" }}>
                          <div className="notif-row-actions">
                            {item.status === "FAILED" && !item.is_permanent_fail && (
                              <button
                                className="notif-action-btn notif-action-btn--retry"
                                onClick={() => handleRetryLog(item._id)}
                                disabled={retryingId === item._id}
                                title="Re-enqueue for delivery"
                              >
                                <IconRotateCw size={12} className={retryingId === item._id ? "notif-btn__icon--spin" : ""} />
                                <span>{retryingId === item._id ? "…" : "Retry"}</span>
                              </button>
                            )}

                            {item.booking_id ? (
                              <button
                                className="notif-booking-btn"
                                onClick={() => handleViewBooking(item.booking_id)}
                                title={`View booking ${item.booking_id}`}
                              >
                                <svg className="notif-booking-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                                <span>View</span>
                              </button>
                            ) : (
                              <span className="notif-booking-empty">—</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalDocs={totalDocs}
            pageSize={PAGE_SIZE}
            onPageChange={setCurrentPage}
            loading={loading}
          />
        </div>

        {/* ── Mobile Filter Modal ─────────────────────────────────────────────── */}
        {showFilterModal && (
          <div className="booking-modal-overlay" onClick={() => setShowFilterModal(false)}>
            <div className="booking-modal booking-modal--filter" onClick={(e) => e.stopPropagation()}>
              <div className="booking-modal__header">
                <h3 className="booking-modal__title">Filter Notifications</h3>
                <button className="booking-modal__close-btn" onClick={() => setShowFilterModal(false)}>×</button>
              </div>

              <div className="booking-modal__body booking-modal__body--filter">
                <FilterDropdown
                  label="Notification Type"
                  value={tempTypeFilter}
                  options={TYPE_OPTIONS}
                  onChange={setTempTypeFilter}
                  minWidth="100%"
                />

                <FilterDropdown
                  label="Delivery Status"
                  value={tempDeliveryFilter}
                  options={DELIVERY_OPTIONS}
                  onChange={setTempDeliveryFilter}
                  minWidth="100%"
                />

                <FilterDropdown
                  label="Error Code"
                  value={tempErrorFilter}
                  options={ERROR_OPTIONS}
                  onChange={setTempErrorFilter}
                  minWidth="100%"
                />

                <FilterDropdown
                  label="Recipient"
                  value={tempRecipientFilter}
                  options={RECIPIENT_OPTIONS}
                  onChange={setTempRecipientFilter}
                  minWidth="100%"
                />
              </div>

              <div className="booking-modal__footer booking-modal__footer--filter">
                <button
                  className="booking-btn booking-btn--secondary"
                  onClick={() => {
                    setTempTypeFilter("All");
                    setTempStatusFilter("All");
                    setTempDeliveryFilter("All");
                    setTempErrorFilter("All");
                    setTempRecipientFilter("All");
                    setTypeFilter("All");
                    setStatusFilter("All");
                    setDeliveryFilter("All");
                    setErrorFilter("All");
                    setRecipientFilter("All");
                    setCurrentPage(1);
                    setShowFilterModal(false);
                  }}
                >
                  Reset
                </button>

                <button
                  className="booking-btn booking-btn--primary"
                  onClick={() => {
                    setTypeFilter(tempTypeFilter);
                    setStatusFilter(tempStatusFilter);
                    setDeliveryFilter(tempDeliveryFilter);
                    setErrorFilter(tempErrorFilter);
                    setRecipientFilter(tempRecipientFilter);
                    setCurrentPage(1);
                    setShowFilterModal(false);
                  }}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Booking Detail Modal ────────────────────────────────────────────── */}
        {selectedBooking && (
          <div className="notif-modal-overlay" onClick={() => setSelectedBooking(null)}>
            <div className="notif-modal" onClick={(e) => e.stopPropagation()}>
              <div className="notif-modal__header">
                <div>
                  <h3 className="notif-modal__title">
                    Booking #{selectedBooking.booking_code || selectedBooking._id}
                  </h3>
                  <span className="notif-modal__subtitle">
                    <span>{selectedBooking.function_type || "Event"}</span>
                    &middot;
                    <span className={`notif-status-chip notif-status-chip--${(selectedBooking.status || "").toLowerCase()}`}>
                      {selectedBooking.status || "Unknown"}
                    </span>
                  </span>
                </div>
                <button className="notif-modal__close-btn" onClick={() => setSelectedBooking(null)}>×</button>
              </div>

              <div className="notif-modal__body">
                {selectedBooking.loading || loadingBooking ? (
                  <div className="notif-modal__loading">
                    <div className="app-spinner" />
                    <span>Loading booking details...</span>
                  </div>
                ) : (
                  <div className="notif-booking-details">
                    <div className="notif-booking-grid">
                      <div className="notif-detail-field">
                        <span className="notif-detail-field__label">Client Name</span>
                        <span className="notif-detail-field__val">{selectedBooking.client_name || "—"}</span>
                      </div>
                      <div className="notif-detail-field">
                        <span className="notif-detail-field__label">WhatsApp Phone</span>
                        <span className="notif-detail-field__val">
                          {selectedBooking.client_phone ? `+${selectedBooking.client_phone}` : "—"}
                        </span>
                      </div>
                      <div className="notif-detail-field">
                        <span className="notif-detail-field__label">Client Email</span>
                        <span className="notif-detail-field__val">{selectedBooking.client_email || "—"}</span>
                      </div>
                      <div className="notif-detail-field">
                        <span className="notif-detail-field__label">Event Date &amp; Time</span>
                        <span className="notif-detail-field__val">
                          {formatDateOnly(selectedBooking.function_date)} &middot; {selectedBooking.function_time || "—"}
                        </span>
                      </div>
                      <div className="notif-detail-field notif-detail-field--full">
                        <span className="notif-detail-field__label">Venue / Address</span>
                        <span className="notif-detail-field__val">{selectedBooking.function_address || "—"}</span>
                      </div>
                      <div className="notif-detail-field">
                        <span className="notif-detail-field__label">Package Price</span>
                        <span className="notif-detail-field__val notif-detail-field__val--price">
                          {formatCurrency(selectedBooking.package_price || selectedBooking.amount)}
                        </span>
                      </div>
                      <div className="notif-detail-field">
                        <span className="notif-detail-field__label">Status</span>
                        <div className="notif-detail-field__val">
                          <span className={`notif-status-chip notif-status-chip--${(selectedBooking.status || "").toLowerCase()}`}>
                            {selectedBooking.status || "—"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="notif-modal__footer">
                <button className="notif-btn notif-btn--secondary" onClick={() => setSelectedBooking(null)}>Close</button>
                <button
                  className="notif-btn notif-btn--primary"
                  onClick={() => navigate(`/admin/bookings?search=${encodeURIComponent(selectedBooking.booking_code || selectedBooking._id)}`)}
                >
                  Open in Bookings
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Today's Reminders Modal ─────────────────────────────────────────── */}
        {showTodayRemindersModal && (
          <div className="notif-modal-overlay" onClick={() => setShowTodayRemindersModal(false)}>
            <div className="notif-modal notif-modal--wide" onClick={(e) => e.stopPropagation()}>
              <div className="notif-modal__header">
                <div>
                  <h3 className="notif-modal__title">Today's Event Reminders</h3>
                  <span className="notif-modal__subtitle">
                    {todayRemindersDate ? `Events scheduled for today (${todayRemindersDate})` : "Events scheduled for today"}
                  </span>
                </div>
                <button className="notif-modal__close-btn" onClick={() => setShowTodayRemindersModal(false)}>×</button>
              </div>

              <div className="notif-modal__body">
                {loadingTodayReminders ? (
                  <div className="notif-modal__loading">
                    <div className="app-spinner" />
                    <span>Loading today's events...</span>
                  </div>
                ) : todayReminders.length > 0 ? (
                  <>
                    <div className="today-reminders-banner">
                      <span>Found <strong>{todayReminders.length}</strong> confirmed upcoming event(s) for today.</span>
                    </div>

                    <div className="today-reminders-list">
                      {todayReminders.map((r, idx) => (
                        <div key={r.booking_code || idx} className="today-reminder-item">
                          <div className="today-reminder-top">
                            <div>
                              <span className="today-reminder-client">{r.client_name}</span>
                              <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginLeft: "0.5rem" }}>
                                ({r.function_type})
                              </span>
                            </div>
                            <span className="notif-booking-pill">{r.booking_code}</span>
                          </div>

                          <div className="today-reminder-meta">
                            <span>
                              <IconClock size={12} />
                              {r.function_time || "Time not specified"}
                            </span>
                            <span>
                              <IconMapPin size={12} />
                              {r.function_address || "Venue not specified"}
                            </span>
                            <span>
                              <IconPhone size={12} />
                              {r.client_phone ? `+${r.client_phone}` : "No phone"}
                            </span>
                          </div>

                          <div className="today-reminder-chips">
                            <span className={`today-chip ${r.customer_reminder_sent ? "today-chip--sent" : "today-chip--pending"}`}>
                              {r.customer_reminder_sent ? (
                                <><IconCheck size={10} /> Client: Sent</>
                              ) : (
                                <>
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="12 6 12 12 16 14" />
                                  </svg>
                                  Client: Pending
                                </>
                              )}
                            </span>
                            <span className={`today-chip ${r.admin_reminder_sent ? "today-chip--sent" : "today-chip--pending"}`}>
                              {r.admin_reminder_sent ? (
                                <><IconCheck size={10} /> Admin: Sent</>
                              ) : (
                                <>
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="12 6 12 12 16 14" />
                                  </svg>
                                  Admin: Pending
                                </>
                              )}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="today-reminders-empty">
                    <svg className="today-reminders-empty__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                    </svg>
                    <h3 className="today-reminders-empty__title">No Pending Reminders</h3>
                    <p className="today-reminders-empty__text">
                      All confirmed events for today have already had reminders sent, or there are no upcoming events scheduled after the current time.
                    </p>
                  </div>
                )}
              </div>

              <div className="notif-modal__footer">
                <button className="notif-btn notif-btn--secondary" onClick={() => setShowTodayRemindersModal(false)}>Close</button>
                {todayReminders.length > 0 && (
                  <button
                    className="notif-btn notif-btn--primary"
                    onClick={handleTriggerReminders}
                    disabled={triggeringReminders}
                  >
                    {triggeringReminders ? "Sending…" : "Send Reminders Now"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Toast ──────────────────────────────────────────────────────────── */}
        {toastMessage && (
          <div className="notif-toast">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" color="#c9a96e">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <span>{toastMessage}</span>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
